import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeftOutlined, DeleteOutlined, DownOutlined, EditOutlined, EyeOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined, ReloadOutlined, SaveOutlined, UploadOutlined, UpOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Form, Image, Input, InputNumber, message, Modal, Popconfirm, Radio, Row, Select, Space, Spin, Switch, Table, Tag, Tooltip, Typography, Upload } from "antd";
import { useHistory, useLocation, useParams } from "react-router-dom";
import moment from "moment";
import { api, AUTH_ROLE_KEY } from "../services/apiClient";
import { promptApi } from "../services/promptApi";

const { Text, Paragraph } = Typography;
const DATE_FORMAT = "DD/MM/YYYY";
const DATE_TIME_FORMAT = "DD/MM/YYYY HH:mm";
const defaultStartDate = () => moment().subtract(2, "months").startOf("day");
const normalizeTime = (value) => (value && value.length === 5 ? `${value}:00` : value);
const hourOptions = Array.from({ length: 24 }, (_, hour) => ({ value: hour, label: `${hour}`.padStart(2, "0") }));
const minuteOptions = Array.from({ length: 12 }, (_, index) => index * 5).map((minute) => ({ value: minute, label: `${minute}`.padStart(2, "0") }));
const categoryOptions = ["GAMING", "TECH", "BLOG"].map((value) => ({ value, label: value }));
const RESERVED_FFMPEG_PARAMS = ["input", "output"];
const videoStatusMeta = {
  PENDING_DOWNLOAD: { label: "Chờ đến lịch", color: "gold" },
  DOWNLOADING: { label: "Đang download", color: "processing" },
  DOWNLOADED: { label: "Đã download", color: "cyan" },
  RENDERING: { label: "Đang render", color: "processing" },
  READY_TO_UPLOAD: { label: "Đã render", color: "blue" },
  UPLOADING: { label: "Đang upload", color: "processing" },
  COMPLETED: { label: "Đã upload", color: "green" },
  FAILED: { label: "Lỗi", color: "red" },
  DOWNLOAD_FAILED: { label: "Lỗi download", color: "red" },
};

function labelOf(item, fields) {
  return fields.map((field) => item?.[field]).find(Boolean) || item?.id;
}

function toDateTimeInputValue(value) {
  if (!value) return undefined;
  const date = moment(value);
  return date.isValid() ? date : undefined;
}

function fromDateTimeInputValue(value) {
  if (!value) return null;
  if (moment.isMoment(value)) return value.format("YYYY-MM-DDTHH:mm:ss");
  return new Date(value).toISOString().slice(0, 19);
}

function toDatePickerValue(value) {
  if (!value) return undefined;
  const date = moment(value, "YYYY-MM-DD", true);
  return date.isValid() ? date : moment(value);
}

function fromDatePickerValue(value) {
  if (!value) return null;
  return moment.isMoment(value) ? value.format("YYYY-MM-DD") : value;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = moment(value);
  return date.isValid() ? date.format(DATE_TIME_FORMAT) : "—";
}

function sortByScheduledTime(items = []) {
  return [...items].sort((first, second) => {
    const firstTime = first.scheduledTime ? moment(first.scheduledTime).valueOf() : Number.MAX_SAFE_INTEGER;
    const secondTime = second.scheduledTime ? moment(second.scheduledTime).valueOf() : Number.MAX_SAFE_INTEGER;
    if (firstTime !== secondTime) return firstTime - secondTime;
    return String(first.id || "").localeCompare(String(second.id || ""));
  });
}

function youtubeVideoUrl(videoId) {
  return videoId ? `https://www.youtube.com/watch?v=${videoId}` : null;
}

export function buildAutoConfigPayload(values = {}, { id, providers = [], assistants = [] } = {}) {
  const contentGenerationMode = values.contentGenerationMode === "AI" ? "AI" : "MANUAL";
  const enabledProviders = providers.filter((item) => item?.enabled !== false);
  const selectedProvider = enabledProviders.find((item) => String(item.id) === String(values.aiProviderId));
  const defaultProvider = selectedProvider || enabledProviders.find((item) => item.defaultProvider) || enabledProviders[0];
  const enabledAssistants = assistants.filter((item) => item?.enabled !== false);
  const selectedAssistant = enabledAssistants.find((item) => String(item.id) === String(values.aiAssistantId));
  const defaultAssistant = selectedAssistant || enabledAssistants[0];
  const youtubeChannelIds = values.youtubeChannelIds || [];
  return {
    ...values,
    id,
    contentGenerationMode,
    aiProviderId: contentGenerationMode === "AI" ? defaultProvider?.id : null,
    aiAssistantId: contentGenerationMode === "AI" ? defaultAssistant?.id : null,
    aiContentBrief: contentGenerationMode === "AI" ? values.aiContentBrief?.trim() || "Tạo nội dung phù hợp với tiêu đề và mô tả của video nguồn." : null,
    startDate: fromDatePickerValue(values.startDate),
    privacy: "PUBLIC",
    sourceType: values.sourceType || "YOUTUBE",
    sourceFiles: values.sourceFiles || [],
    facebookCookieFile: values.facebookCookieFile || null,
    category: "BLOG",
    uploadGroupId: null,
    scheduleType: values.scheduleType || "RANDOM",
    scheduleHours: values.scheduleHours || [],
    scheduleMinutes: values.scheduleMinutes || [],
    startTime: normalizeTime(values.startTime),
    endTime: normalizeTime(values.endTime),
    targetChannel: youtubeChannelIds[0] || undefined,
    youtubeChannelIds,
    tiktokAccountIds: values.tiktokAccountIds || [],
    facebookPageIds: values.facebookPageIds || [],
    ffmpegCommands: (values.ffmpegCommands || []).filter((item) => item?.commandId || item?.commandCode),
  };
}

function youtubeChannelUrl(channelId) {
  return channelId ? `https://www.youtube.com/channel/${channelId}` : null;
}

function youtubeThumbnailUrl(videoId) {
  return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
}

function facebookVideoUrl(videoId) {
  return videoId ? `https://www.facebook.com/${videoId}` : null;
}

function localPreviewUrl(path) {
  return path ? `http://127.0.0.1:18080/preview?path=${encodeURIComponent(path)}` : null;
}

function isErrorStatus(status) {
  return Boolean(status && String(status).includes("FAILED"));
}

function queueTargetKey(video) {
  return `${video.uploadChannelType || "YOUTUBE"}:${video.uploadChannelId || video.uploadChannelName || ""}`;
}

export default function AutoConfigDetail() {
  const { id } = useParams();
  const isNew = id === "new";
  const history = useHistory();
  const location = useLocation();
  const isView = new URLSearchParams(location.search).get("mode") === "view";
  const [form] = Form.useForm();
  const [queueForm] = Form.useForm();
  const [channels, setChannels] = useState([]);
  const [tiktokAccounts, setTiktokAccounts] = useState([]);
  const [facebookPages, setFacebookPages] = useState([]);
  const [ffmpegCommandTemplates, setFfmpegCommandTemplates] = useState([]);
  const [aiProviders, setAiProviders] = useState([]);
  const [aiAssistants, setAiAssistants] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueChannelFilter, setQueueChannelFilter] = useState();
  const [queuePagination, setQueuePagination] = useState({ current: 1, pageSize: 10 });
  const [editingVideo, setEditingVideo] = useState(null);
  const [queueError, setQueueError] = useState(null);
  const [queueSaving, setQueueSaving] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [sourceUploadFiles, setSourceUploadFiles] = useState([]);
  const [facebookCookieFile, setFacebookCookieFile] = useState(null);
  const [minioUploadingId, setMinioUploadingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({ source: true, render: true, upload: true });

  const applyConfigDetail = useCallback((result) => {
    const item = result?.data || result;
    const targetChannelId = item.targetChannel?.id || item.targetChannel;
    const youtubeChannelIds = item.youtubeChannelIds?.length ? item.youtubeChannelIds : targetChannelId ? [targetChannelId] : [];
    setVideos(sortByScheduledTime(item.videos || []));
    form.setFieldsValue({
      ...item,
      sourceUrls: item.sourceType === "UPLOAD" ? "uploaded://source" : item.sourceUrls,
      startDate: toDatePickerValue(item.startDate),
      scheduleType: item.scheduleType === "RANDOM" ? "RANDOM" : "FIXED",
      startTime: item.startTime?.slice(0, 5),
      endTime: item.endTime?.slice(0, 5),
      youtubeChannelIds,
      tiktokAccountIds: item.tiktokAccountIds || [],
      facebookPageIds: item.facebookPageIds || [],
      scheduleHours: item.scheduleHours || [],
      scheduleMinutes: item.scheduleMinutes || [],
      ffmpegCommands: item.ffmpegCommands?.length ? item.ffmpegCommands : [],
      sourceType: item.sourceType || "YOUTUBE",
      sourceFiles: item.sourceFiles || [],
      facebookCookieFile: item.facebookCookieFile || null,
    });
    setSourceUploadFiles((item.sourceFiles || []).map((file, index) => ({
      uid: file.uid || `source-${index}`,
      name: file.fileName || "video",
      status: "done",
      url: file.url,
      response: file,
    })));
    setFacebookCookieFile(item.facebookCookieFile || null);
  }, [form]);

  const loadConfigDetail = useCallback(async ({ silent = false } = {}) => {
    if (isNew) return;
    if (silent) {
      setQueueLoading(true);
    } else {
      setLoading(true);
    }
    try {
      const result = await api.get(`/api/configs/${id}`);
      applyConfigDetail(result);
    } catch (error) {
      message.error(error.message);
    } finally {
      if (silent) {
        setQueueLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [applyConfigDetail, id, isNew]);

  useEffect(() => {
    Promise.all([
      api.get("/api/channels").then(setChannels),
      api.get("/api/tiktok-accounts").then(setTiktokAccounts),
      api.get("/api/facebook-pages").then(setFacebookPages),
      api.get("/api/video-tools/ffmpeg-commands").then(setFfmpegCommandTemplates),
      promptApi.providers.list().then(setAiProviders),
      promptApi.assistants.list().then(setAiAssistants),
    ]).catch((error) => message.error(error.message));

    if (!isNew) {
      loadConfigDetail();
    }
  }, [isNew, loadConfigDetail]);

  const ffmpegTemplateById = useMemo(() => Object.fromEntries(ffmpegCommandTemplates.map((item) => [item.id, item])), [ffmpegCommandTemplates]);
  const ffmpegTemplateOptions = useMemo(
    () => ffmpegCommandTemplates.map((item) => ({ value: item.id, label: item.commandName })),
    [ffmpegCommandTemplates]
  );
  const queueChannelOptions = useMemo(() => {
    const channelMap = new Map();
    videos.forEach((video) => {
      const value = queueTargetKey(video);
      const type = video.uploadChannelType || "YOUTUBE";
      const label = `${type}: ${video.uploadChannelName || video.uploadChannelId}`;
      if (value && !channelMap.has(value)) channelMap.set(value, { value, label });
    });
    return Array.from(channelMap.values());
  }, [videos]);
  const filteredVideos = useMemo(() => {
    const items = queueChannelFilter
      ? videos.filter((video) => queueTargetKey(video) === queueChannelFilter)
      : videos;
    return sortByScheduledTime(items);
  }, [queueChannelFilter, videos]);
  const isSuperAdmin = localStorage.getItem(AUTH_ROLE_KEY) === "SUPER_ADMIN";

  const applyFfmpegTemplate = (fieldName, commandId) => {
    const template = ffmpegTemplateById[commandId];
    const commands = [...(form.getFieldValue("ffmpegCommands") || [])];
    const current = commands[fieldName] || {};
    commands[fieldName] = template
      ? {
          commandId: template.id,
          commandName: template.commandName,
          commandCode: template.commandCode,
          parameters: template.parameters || [],
          description: template.description || "",
          parameterValues: current.parameterValues || {},
        }
      : {};
    form.setFieldsValue({ ffmpegCommands: commands });
  };

  const uploadFfmpegAsset = async (fieldName, parameter, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const result = await api.upload("/api/video-tools/ffmpeg-commands/assets", formData);
    const commands = [...(form.getFieldValue("ffmpegCommands") || [])];
    const command = { ...(commands[fieldName] || {}) };
    command.parameterValues = {
      ...(command.parameterValues || {}),
      [parameter]: result?.data || result,
    };
    commands[fieldName] = command;
    form.setFieldsValue({ ffmpegCommands: commands });
    message.success(`Uploaded ${parameter}`);
  };

  const uploadSourceFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const result = await api.upload("/api/configs/source-files", formData);
    const uploaded = result?.data || result;
    const nextFiles = [...(form.getFieldValue("sourceFiles") || []), uploaded];
    form.setFieldsValue({ sourceFiles: nextFiles });
    setSourceUploadFiles((items) => [...items, {
      uid: file.uid || `${Date.now()}-${file.name}`,
      name: uploaded.fileName || file.name,
      status: "done",
      url: uploaded.url,
      response: uploaded,
    }]);
  };

  const uploadFacebookCookie = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const result = await api.upload("/api/configs/source-files", formData);
    const uploaded = result?.data || result;
    form.setFieldsValue({ facebookCookieFile: uploaded });
    setFacebookCookieFile(uploaded);
    message.success("Đã tải cookie Facebook");
  };

  const renderFfmpegAssetInputs = (fieldName) => {
    const command = (form.getFieldValue("ffmpegCommands") || [])[fieldName] || {};
    const parameters = (command.parameters || []).filter((item) => !RESERVED_FFMPEG_PARAMS.includes(item));
    if (!parameters.length) return null;
    return (
      <div className="ffmpeg-asset-list">
        {parameters.map((parameter) => {
          const value = command.parameterValues?.[parameter];
          return (
            <div className="ffmpeg-asset-row" key={parameter}>
              <Text strong>{parameter}</Text>
              <Input disabled value={value?.fileName || value?.url || ""} placeholder="Select local file" />
              {!isView && (
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  showUploadList={false}
                  customRequest={({ file, onSuccess, onError }) => {
                    uploadFfmpegAsset(fieldName, parameter, file)
                      .then(() => onSuccess && onSuccess("ok"))
                      .catch((error) => {
                        message.error(error.message);
                        if (onError) onError(error);
                      });
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload</Button>
                </Upload>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const toggleSection = (key) => {
    setOpenSections((value) => ({ ...value, [key]: !value[key] }));
  };

  const sectionToggle = (key) => (
    <Button
      aria-label={openSections[key] ? "Thu gọn" : "Mở rộng"}
      className="auto-section-toggle"
      icon={openSections[key] ? <UpOutlined /> : <DownOutlined />}
      onClick={() => toggleSection(key)}
      type="text"
    />
  );

  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await api.post("/api/configs/create-or-updadte", buildAutoConfigPayload(values, {
        id: isNew ? undefined : id,
        providers: aiProviders,
        assistants: aiAssistants,
      }));
      message.success("Đã lưu cấu hình");
      history.push("/auto-upload/configs");
    } catch (error) {
      if (error.message) message.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const openQueueEdit = (video) => {
    setEditingVideo(video);
    queueForm.setFieldsValue({
      sourceUrl: video.sourceUrl,
      scheduledTime: toDateTimeInputValue(video.scheduledTime),
      customTitle: video.customTitle,
      customTags: video.customTags,
      customDescription: video.customDescription,
      category: video.category || form.getFieldValue("category") || "GAMING",
    });
  };

  const saveQueueItem = async () => {
    try {
      const values = await queueForm.validateFields();
      setQueueSaving(true);
      await api.put(`/api/videos/${editingVideo.id}`, {
        ...values,
        scheduledTime: fromDateTimeInputValue(values.scheduledTime),
      });
      message.success("Đã cập nhật video trong queue");
      setEditingVideo(null);
      await loadConfigDetail({ silent: true });
    } catch (error) {
      if (error.message) message.error(error.message);
    } finally {
      setQueueSaving(false);
    }
  };

  const deleteQueueItem = async (video) => {
    try {
      await api.delete(`/api/videos/${video.id}`);
      message.success("Đã xoá video khỏi queue");
      await loadConfigDetail({ silent: true });
    } catch (error) {
      message.error(error.message);
    }
  };

  const playQueueItem = async (video) => {
    try {
      await api.post(`/api/videos/${video.id}/play-now`, {});
      message.success("Đã ưu tiên chạy video này");
      await loadConfigDetail({ silent: true });
    } catch (error) {
      message.error(error.message);
    }
  };

  const pauseQueueItem = async (video) => {
    try {
      await api.post(`/api/videos/${video.id}/pause`, {});
      message.success("Đã tạm dừng video này");
      await loadConfigDetail({ silent: true });
    } catch (error) {
      message.error(error.message);
    }
  };

  const uploadQueueThumbnail = async ({ file, onSuccess, onError }) => {
    if (!editingVideo?.id) {
      onError?.(new Error("Chưa chọn video"));
      return;
    }
    try {
      setThumbnailUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      const result = await api.upload(`/api/videos/${editingVideo.id}/thumbnail`, formData);
      const updatedVideo = result?.data || result;
      setEditingVideo(updatedVideo);
      setVideos((items) => sortByScheduledTime(items.map((item) => (item.id === updatedVideo.id ? updatedVideo : item))));
      message.success("Đã upload thumbnail");
      onSuccess?.(updatedVideo);
    } catch (error) {
      message.error(error.message);
      onError?.(error);
    } finally {
      setThumbnailUploading(false);
    }
  };

  const uploadMinioPreview = async (video) => {
    try {
      setMinioUploadingId(video.id);
      await api.post(`/api/videos/${video.id}/minio-preview`, {});
      message.success("Đã gửi yêu cầu upload preview lên Minio");
      await loadConfigDetail({ silent: true });
    } catch (error) {
      message.error(error.message);
    } finally {
      setMinioUploadingId(null);
    }
  };

  const renderMinioPreviewAction = (item) => {
    if (!isSuperAdmin) return null;
    if (item.previewUrl) {
      return (
        <Tooltip title="Preview Minio">
          <Button
            aria-label="Preview Minio"
            className="provider-action-button minio-preview-button"
            href={item.previewUrl}
            icon={<EyeOutlined />}
            rel="noreferrer"
            target="_blank"
            type="text"
          />
        </Tooltip>
      );
    }
    if (item.minioPreviewStatus === "UPLOADING" || item.minioPreviewStatus === "REQUESTED") {
      return (
        <Tooltip title="Đang upload preview lên Minio">
          <Button
            aria-label="Đang upload preview lên Minio"
            className="provider-action-button minio-preview-button"
            loading
            type="text"
          />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Upload to Minio">
        <Button
          aria-label="Upload to Minio"
          className="provider-action-button minio-preview-button"
          icon={<UploadOutlined />}
          loading={minioUploadingId === item.id}
          onClick={() => uploadMinioPreview(item)}
          type="text"
        />
      </Tooltip>
    );
  };

  const targetFieldNames = ["youtubeChannelIds", "tiktokAccountIds", "facebookPageIds"];
  const validateUploadTargets = () => {
    const hasTarget = targetFieldNames.some((field) => (form.getFieldValue(field) || []).length > 0);
    return hasTarget ? Promise.resolve() : Promise.reject(new Error("Chọn ít nhất 1 kênh, account hoặc page"));
  };
  const revalidateUploadTargets = () => {
    form.validateFields(targetFieldNames).catch(() => {});
  };

  const renderQueueStatus = (value, item) => {
    const meta = videoStatusMeta[value] || { label: value || "Chờ đến lịch", color: "default" };
    const tag = <Tag color={meta.color}>{meta.label}</Tag>;
    if (!isErrorStatus(value) || !item?.errorMessage) return tag;
    return (
      <Tooltip title="Xem thông tin lỗi">
        <span className="queue-error-status" onClick={() => setQueueError(item)}>
          {tag}
        </span>
      </Tooltip>
    );
  };

  const videoColumns = [
    {
      title: "STT",
      width: 56,
      align: "center",
      className: "processed-queue-index-cell",
      render: (_, __, index) => ((queuePagination.current || 1) - 1) * (queuePagination.pageSize || 10) + index + 1,
    },
    {
      title: "VIDEO GỐC",
      width: "17%",
      render: (_, item) => {
        const title = item.customTitle || item.sourceUrl || "Video chưa có tiêu đề";
        return item.sourceUrl
          ? <a className="processed-source-link" href={item.sourceUrl} target="_blank" rel="noreferrer">{title}</a>
          : <Text strong>{title}</Text>;
      },
    },
    {
      title: "VIDEO UPLOAD",
      width: "14%",
      render: (_, item) => {
        if (form.getFieldValue("sourceType") === "UPLOAD") return <Text>Máy cá nhân</Text>;
        const uploadVideo = item.renderPath || item.filePath;
        const localUrl = localPreviewUrl(uploadVideo);
        if (item.thumbnailUrl) {
          const thumbnail = <Image className="processed-thumbnail" preview={false} src={item.thumbnailUrl} width={92} height={52} />;
          return (
            <div className="processed-video-cell">
              {localUrl ? <a href={localUrl} target="_blank" rel="noreferrer">{thumbnail}</a> : thumbnail}
            </div>
          );
        }
        if (localUrl) {
          return (
            <div className="processed-video-cell">
              <Text className="processed-video-path">{uploadVideo}</Text>
            </div>
          );
        }
        return uploadVideo ? <Text className="processed-video-path">{uploadVideo}</Text> : <Text type="secondary">Chưa có file</Text>;
      },
    },
    {
      title: "VIDEO THÀNH CÔNG",
      width: "10%",
      render: (_, item) => {
        const facebook = item.uploadChannelType === "FACEBOOK";
        const value = facebook ? item.facebookVideoId : item.youtubeVideoId;
        const url = facebook ? facebookVideoUrl(value) : youtubeVideoUrl(value);
        if (!url) return <Text type="secondary">—</Text>;
        return (
          <a href={url} target="_blank" rel="noreferrer">
            {facebook ? <Text className="processed-channel-link">Xem video Facebook</Text> : <Image className="processed-thumbnail" preview={false} src={youtubeThumbnailUrl(value)} width={92} height={52} />}
          </a>
        );
      },
    },
    { title: "LOẠI KÊNH", width: "8%", dataIndex: "uploadChannelType", render: (value) => <Tag>{value || "YOUTUBE"}</Tag> },
    {
      title: "KÊNH UPLOAD",
      width: "11%",
      dataIndex: "uploadChannelName",
      render: (value, item) => {
        const label = value || item.uploadChannelId || "—";
        const url = item.uploadChannelType === "YOUTUBE" ? youtubeChannelUrl(item.uploadChannelId) : null;
        return url ? <a className="processed-channel-link" href={url} target="_blank" rel="noreferrer">{label}</a> : label;
      },
    },
    { title: "LỊCH ĐĂNG", width: "9%", align: "center", dataIndex: "scheduledTime", render: (value) => <span className="processed-schedule-time">{formatDateTime(value)}</span> },
    { title: "WORKER", width: "8%", dataIndex: "processingWorkerName", render: (value, item) => value || item.processingWorkerId || "—" },
    { title: "TRẠNG THÁI", width: "9%", dataIndex: "status", render: renderQueueStatus },
    {
      title: "HÀNH ĐỘNG",
      width: "10%",
      render: (_, item) => (
        <Space size={4} className="provider-actions auto-config-actions">
          {item.processingActive ? (
            <Tooltip title="Tạm dừng">
              <Button className="provider-action-button" icon={<PauseCircleOutlined />} type="text" onClick={() => pauseQueueItem(item)} />
            </Tooltip>
          ) : (
            <Tooltip title="Chạy ngay">
              <Button className="provider-action-button" icon={<PlayCircleOutlined />} type="text" onClick={() => playQueueItem(item)} />
            </Tooltip>
          )}
          <Tooltip title="Sửa">
            <Button className="provider-action-button" icon={<EditOutlined />} type="text" onClick={() => openQueueEdit(item)} />
          </Tooltip>
          <Popconfirm title="Xoá video này khỏi queue?" okText="Xoá" cancelText="Huỷ" onConfirm={() => deleteQueueItem(item)}>
            <Button className="provider-action-button" danger icon={<DeleteOutlined />} type="text" />
          </Popconfirm>
          {renderMinioPreviewAction(item)}
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Form
        className={isView ? "auto-config-readonly" : undefined}
        form={form}
        layout="vertical"
        initialValues={{
          autoFollow: true,
          privacy: "PUBLIC",
          category: "BLOG",
          scheduleType: "RANDOM",
          startDate: defaultStartDate(),
          startTime: "12:00",
          endTime: "18:00",
          intervalHourFrom: 4,
          intervalHourTo: 6,
          scheduleHours: [12],
          scheduleMinutes: [0],
          youtubeChannelIds: [],
          tiktokAccountIds: [],
          facebookPageIds: [],
          ffmpegCommands: [],
          sourceType: "YOUTUBE",
          sourceFiles: [],
          facebookCookieFile: null,
          contentGenerationMode: "MANUAL",
        }}
      >
        <div className="page-heading auto-config-detail-heading">
          <Space>
            <Button className="flow-icon-button" icon={<ArrowLeftOutlined />} onClick={() => history.push("/auto-upload/configs")} />
            <Form.Item className="auto-config-title-item" name="configurationName" rules={[{ required: true, message: "Nhập tên cấu hình" }]}>
              <Input aria-label="Tên cấu hình" disabled={isView} placeholder="Tên cấu hinh Reup" />
            </Form.Item>
          </Space>
          {!isView && <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Lưu cấu hình</Button>}
        </div>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card className="criclebox mb-24 auto-config-section" title="1. Nguồn video" extra={sectionToggle("source")}>
              {openSections.source && (
                <>
                  <Form.Item name="sourceType" label="Loại nguồn">
                    <Radio.Group onChange={(event) => {
                      if (event.target.value === "UPLOAD") form.setFieldsValue({ sourceUrls: "uploaded://source" });
                    }}>
                      <Radio value="YOUTUBE">Youtube</Radio>
                      <Radio value="FACEBOOK">Facebook</Radio>
                      <Radio value="UPLOAD">Upload video</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(previous, current) => previous.sourceType !== current.sourceType}>
                    {() => form.getFieldValue("sourceType") === "UPLOAD" && (
                      <>
                        <Form.Item name="sourceFiles" hidden rules={[{ validator: (_, value) => value?.length ? Promise.resolve() : Promise.reject(new Error("Chọn ít nhất 1 video")) }]}><Input /></Form.Item>
                        <Form.Item label="Video upload">
                          <Upload
                            multiple
                            beforeUpload={(file) => {
                              uploadSourceFile(file).catch((error) => message.error(error.message));
                              return false;
                            }}
                            fileList={sourceUploadFiles}
                            showUploadList={{ showRemoveIcon: true }}
                            onRemove={(file) => {
                              const nextFiles = (form.getFieldValue("sourceFiles") || []).filter((item) => item.url !== file.response?.url);
                              form.setFieldsValue({ sourceFiles: nextFiles });
                              setSourceUploadFiles((items) => items.filter((item) => item.uid !== file.uid));
                            }}
                          >
                            <Button icon={<UploadOutlined />}>Chọn nhiều video</Button>
                          </Upload>
                        </Form.Item>
                      </>
                    )}
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(previous, current) => previous.sourceType !== current.sourceType}>
                    {() => form.getFieldValue("sourceType") !== "UPLOAD" && (
                      form.getFieldValue("sourceType") === "FACEBOOK" ? (
                        <Row gutter={16} align="top">
                          <Col xs={24} md={18}>
                            <Form.Item name="sourceUrls" label="URL nguồn" rules={[{ required: true, message: "Nhập URL nguồn" }]}>
                              <Input.TextArea rows={3} placeholder="Mỗi URL Facebook một dòng" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}>
                            <Form.Item className="facebook-cookie-item" label="Cookie Facebook">
                              <div className="facebook-cookie-control">
                                <Upload
                                  accept=".txt"
                                  maxCount={1}
                                  beforeUpload={(file) => {
                                    uploadFacebookCookie(file).catch((error) => message.error(error.message));
                                    return false;
                                  }}
                                  showUploadList={false}
                                >
                                  <Button icon={<UploadOutlined />}>Chọn cookie.txt</Button>
                                </Upload>
                                <Text type="secondary" className="facebook-cookie-help">Cần cookie.txt để tải video</Text>
                                {facebookCookieFile?.fileName && <Text type="success" className="facebook-cookie-name">{facebookCookieFile.fileName}</Text>}
                              </div>
                            </Form.Item>
                          </Col>
                        </Row>
                      ) : (
                        <Form.Item name="sourceUrls" label="URL nguồn" rules={[{ required: true, message: "Nhập URL nguồn" }]}>
                          <Input.TextArea rows={3} placeholder="Mỗi URL một dòng hoặc nguồn playlist/channel" />
                        </Form.Item>
                      )
                    )}
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(previous, current) => previous.sourceType !== current.sourceType}>
                    {() => form.getFieldValue("sourceType") === "YOUTUBE" && (
                      <div className="youtube-source-options">
                        <div className="youtube-source-option">
                          <span className="youtube-source-option-label">Ngày bắt đầu</span>
                          <Form.Item name="startDate" noStyle><DatePicker format={DATE_FORMAT} style={{ width: "100%" }} /></Form.Item>
                        </div>
                        <div className="auto-follow-inline">
                          <span className="auto-follow-label">Tự theo dõi nguồn</span>
                          <Form.Item name="autoFollow" noStyle valuePropName="checked"><Switch /></Form.Item>
                        </div>
                      </div>
                    )}
                  </Form.Item>
                  <Form.Item name="contentGenerationMode" label="Chế độ tạo nội dung">
                    <Radio.Group disabled={isView}>
                      <Radio value="MANUAL">Tạo thủ công</Radio>
                      <Radio value="AI">Tạo bằng AI</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(previous, current) => previous.contentGenerationMode !== current.contentGenerationMode}>
                    {() => form.getFieldValue("contentGenerationMode") !== "AI" && (
                      <>
                        <Form.Item name="titleTemplate" label="Mẫu tiêu đề" extra="Dùng {{number-15}} hoặc {{numer-15}} để bắt đầu đếm từ 15." rules={[{ validator: (_, value) => !value || /\{\{\s*(?:number|numer)(?:-[1-9]\d*)?\s*\}\}/i.test(value) ? Promise.resolve() : Promise.reject(new Error("Mẫu tiêu đề phải có {{number}} hoặc {{number-N}} để không trùng nhau")) }]}><Input disabled={isView} placeholder="Tổng hợp {{xinh | đẹp | hot}} - Phần {{number-15}}" /></Form.Item>
                        <Row gutter={16}>
                          <Col xs={24} md={12}><Form.Item name="tags" label="Tags"><Input.TextArea disabled={isView} rows={5} /></Form.Item></Col>
                          <Col xs={24} md={12}><Form.Item name="descriptionTemplate" label="Mẫu mô tả"><Input.TextArea disabled={isView} rows={5} /></Form.Item></Col>
                        </Row>
                      </>
                    )}
                  </Form.Item>
                  <Row gutter={16}>
                    <Col xs={24} md={12}><Form.Item name="aiTitlePrefix" label="Nối chuỗi trước"><Input disabled={isView} placeholder="Ví dụ: [Review] " /></Form.Item></Col>
                    <Col xs={24} md={12}><Form.Item name="aiTitleSuffix" label="Nối chuỗi sau"><Input disabled={isView} placeholder="Ví dụ: | Thành Phạm review" /></Form.Item></Col>
                  </Row>
                </>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card className="criclebox mb-24 auto-config-section" title="2. Render FFmpeg" extra={sectionToggle("render")}>
              {openSections.render && (
                <>
                  <Form.List name="ffmpegCommands">
                          {(fields, { add, remove }) => (
                            <div className="ffmpeg-command-list">
                              {!isView && (
                                <Button
                                  className="ffmpeg-add-command"
                                  icon={<PlusOutlined />}
                                  onClick={() => add({})}
                                >
                                  Thêm lệnh
                                </Button>
                              )}
                              {fields.map((field, index) => (
                                <div className="ffmpeg-command-block" key={field.key}>
                                  <div className="ffmpeg-command-row">
                                  <Form.Item name={[field.name, "commandId"]} rules={[{ required: true, message: "Chọn lệnh FFmpeg" }]}>
                                    <Select
                                      showSearch
                                      optionFilterProp="label"
                                      placeholder="Chọn lệnh đã khai báo trong Render FFmpeg"
                                      options={ffmpegTemplateOptions}
                                      onChange={(value) => applyFfmpegTemplate(field.name, value)}
                                    />
                                  </Form.Item>
                                  <Form.Item noStyle shouldUpdate>
                                    {() => {
                                      const command = (form.getFieldValue("ffmpegCommands") || [])[field.name] || {};
                                      return <Input disabled value={command.description || ""} placeholder="Mô tả" />;
                                    }}
                                  </Form.Item>
                                  {!isView && (
                                    <Button
                                      aria-label={`Xóa lệnh FFmpeg ${index + 1}`}
                                      className="ffmpeg-delete-command"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => remove(field.name)}
                                      type="text"
                                    />
                                  )}
                                  </div>
                                  <Form.Item noStyle shouldUpdate>
                                    {() => renderFfmpegAssetInputs(field.name)}
                                  </Form.Item>
                                </div>
                              ))}
                            </div>
                          )}
                  </Form.List>
                </>
              )}
            </Card>
          </Col>
        </Row>

        <Card className="criclebox mb-24" title="3. Upload và lịch đăng" extra={sectionToggle("upload")}>
          {openSections.upload && (
            <>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="youtubeChannelIds" label="YouTube Channels" dependencies={targetFieldNames} rules={[{ validator: validateUploadTargets }]}>
                    <Select mode="multiple" placeholder="Chọn kênh YouTube" onChange={revalidateUploadTargets} options={channels.map((item) => ({ value: item.id, label: labelOf(item, ["channelTitle", "channelName", "channelId"]) }))} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="tiktokAccountIds" label="TikTok Accounts" dependencies={targetFieldNames} rules={[{ validator: validateUploadTargets }]}>
                    <Select mode="multiple" placeholder="Chọn account TikTok" onChange={revalidateUploadTargets} options={tiktokAccounts.map((item) => ({ value: item.id, label: labelOf(item, ["displayName", "username", "accountId"]) }))} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="facebookPageIds" label="Facebook Pages" dependencies={targetFieldNames} rules={[{ validator: validateUploadTargets }]}>
                    <Select mode="multiple" placeholder="Chọn page Facebook" onChange={revalidateUploadTargets} options={facebookPages.map((item) => ({ value: item.id, label: labelOf(item, ["pageName", "pageId", "pageUrl"]) }))} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="scheduleType" label="Kiểu lịch">
                    <Select options={[{ value: "FIXED", label: "Cố định" }, { value: "RANDOM", label: "Ngẫu nhiên" }]} />
                  </Form.Item>
                </Col>
                <Form.Item noStyle shouldUpdate>
                  {() =>
                    form.getFieldValue("scheduleType") === "RANDOM" ? (
                      <Col xs={24} md={8}>
                        <Row gutter={8}>
                          <Col xs={12}>
                            <Form.Item name="intervalHourFrom" label="Cách từ">
                              <InputNumber min={1} style={{ width: "100%" }} />
                            </Form.Item>
                          </Col>
                          <Col xs={12}>
                            <Form.Item name="intervalHourTo" label="Đến">
                              <InputNumber min={1} style={{ width: "100%" }} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    ) : (
                      <>
                        <Col xs={24} md={8}>
                          <Form.Item name="scheduleHours" label="Giờ upload">
                            <Select mode="multiple" placeholder="Chọn giờ" options={hourOptions} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="scheduleMinutes" label="Phút upload">
                            <Select mode="multiple" placeholder="Chọn phút" options={minuteOptions} />
                          </Form.Item>
                        </Col>
                      </>
                    )
                  }
                </Form.Item>
              </Row>
            </>
          )}
        </Card>
      </Form>

      {!isNew && isView && (
        <Card
          className="criclebox"
          title={`Danh sách video (${filteredVideos.length})`}
          extra={
            <Space wrap>
              <Select
                allowClear
                placeholder="Lọc kênh upload"
                value={queueChannelFilter}
                onChange={(value) => {
                  setQueueChannelFilter(value);
                  setQueuePagination((current) => ({ ...current, current: 1 }));
                }}
                options={queueChannelOptions}
                style={{ minWidth: 220 }}
              />
              <Button className="flow-icon-button" icon={<ReloadOutlined />} loading={queueLoading} onClick={() => loadConfigDetail({ silent: true })} />
            </Space>
          }
        >
          <Table
            className="processed-queue-table"
            rowKey="id"
            columns={videoColumns}
            dataSource={filteredVideos}
            pagination={queuePagination}
            onChange={(pagination) => setQueuePagination({ current: pagination.current, pageSize: pagination.pageSize })}
          />
        </Card>
      )}
      <Modal
        destroyOnClose
        title="Sửa video trong Danh sách video"
        open={Boolean(editingVideo)}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={queueSaving}
        onCancel={() => setEditingVideo(null)}
        onOk={saveQueueItem}
        width={780}
      >
        <Form form={queueForm} layout="vertical">
          <Form.Item name="sourceUrl" label="Link video gốc" rules={[{ required: true, message: "Nhập link video gốc" }]}>
            <Input placeholder="https://www.youtube.com/watch?v=..." />
          </Form.Item>
          <Form.Item name="scheduledTime" label="Thời gian upload">
            <DatePicker format={DATE_TIME_FORMAT} showTime={{ format: "HH:mm" }} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="customTitle" label="Tiêu đề">
            <Input />
          </Form.Item>
          <Form.Item name="customTags" label="Từ khoá">
            <Input />
          </Form.Item>
          <Form.Item name="customDescription" label="Mô tả">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="category" label="Danh mục">
            <Select options={categoryOptions} />
          </Form.Item>
          <Form.Item label="Thumbnail">
            <Space align="center" wrap>
              {editingVideo?.thumbnailUrl ? <Image src={editingVideo.thumbnailUrl} width={120} height={68} /> : <Text type="secondary">Chưa có thumbnail</Text>}
              <Upload
                accept="image/*"
                customRequest={uploadQueueThumbnail}
                maxCount={1}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />} loading={thumbnailUploading}>Upload thumbnail</Button>
              </Upload>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Thông tin lỗi"
        open={Boolean(queueError)}
        footer={<Button type="primary" onClick={() => setQueueError(null)}>Đóng</Button>}
        onCancel={() => setQueueError(null)}
        width={760}
      >
        <Paragraph copyable style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
          {queueError?.errorMessage || "Không có thông tin lỗi"}
        </Paragraph>
      </Modal>
    </Spin>
  );
}
